/* Shopify Ajax Cart: https://shopify.dev/docs/api/ajax/reference/cart
 * No automatic retries of writes: an ambiguous add remains read-only until reconciled.
 * Download products use quantity one; existing cart lines are never increased or removed.
 */
(function (global) {
  'use strict';
  const unique = ids => [...new Set(ids.map(String))];
  const fail = code => { throw new Error(code); };
  const selectionTotal = months => [...new Map(months.map(m => [String(m.id), m])).values()]
    .reduce((sum, m) => sum + (Number.isSafeInteger(Number(m.price)) && Number(m.price) >= 0 ? Number(m.price) : fail('config_error')), 0);
  const safeRoot = root => {
    if (typeof root !== 'string' || !/^\/(?!\/)[a-zA-Z0-9/_-]*\/$/.test(root) && root !== '/') fail('config_error');
    return root;
  };
  const missingItems = (ids, cart) => {
    if (!cart || !Array.isArray(cart.items)) fail('cart_read');
    return unique(ids).filter(id => !cart.items.some(item => String(item.variant_id) === id && Number(item.quantity) > 0))
      .map(id => ({ id, quantity: 1 }));
  };
  async function addMonths(ids, request, root = '/', state = {}, save = () => {}) {
    root = safeRoot(root);
    ids = unique(ids);
    if (!ids.length) fail('empty');
    if (ids.some(id => !/^[1-9]\d*$/.test(id))) fail('config_error');
    const persist = pending => { save(pending); state.pending = pending; };
    const read = async () => {
      try {
        const r = await request(root + 'cart.js', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!r.ok) fail('cart_read');
        const cart = await r.json(); missingItems(ids, cart); return cart;
      } catch (_) { fail(state.pending ? 'unconfirmed' : 'cart_read'); }
    };
    const before = await read();
    if (state.pending) {
      // A response lost in transit may commit after our read. Absence is NOT proof of failure.
      if (missingItems(state.pending, before).length) fail('unconfirmed');
      persist(null);
    }
    const items = missingItems(ids, before);
    if (!items.length) return { added: 0, alreadyPresent: ids.length, cart: before };
    persist(items.map(item => item.id)); // Persist BEFORE sending, surviving reload/navigation.
    let definiteRejection = false;
    try {
      const response = await request(root + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      definiteRejection = response.status >= 400 && response.status < 500;
      if (!response.ok) fail('add_rejected');
      await response.json();
    } catch (_) {
      const cart = await read();
      if (!missingItems(ids, cart).length) {
        persist(null); return { added: items.length, reconciled: true, cart };
      }
      // A completed 4xx may partially add items. A user retry re-reads and adds only missing IDs.
      if (definiteRejection) { persist(null); fail('retry'); }
      fail('unconfirmed');
    }
    const cart = await read();
    if (missingItems(ids, cart).length) fail('unconfirmed');
    persist(null);
    return { added: items.length, alreadyPresent: ids.length - items.length, cart };
  }
  // CommonJS export is only exercised by the dependency-free VM test harness.
  if (typeof module !== 'undefined') module.exports = { selectionTotal, missingItems, addMonths, safeRoot };
  if (!global.customElements || global.customElements.get('bb-month-picker')) return;
  class MonthPicker extends global.HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.checks = [...this.querySelectorAll('[data-month-id]')];
      this.button = this.querySelector('[data-add-months]');
      this.status = this.querySelector('[data-cart-status]');
      this.cartLink = this.querySelector('[data-view-cart]');
      if (!this.button || !this.status) return;
      try {
        this.labels = JSON.parse(this.querySelector('[data-picker-labels]').textContent);
        this.root = safeRoot(this.dataset.root || '/');
        this.money = n => new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(n / 100);
      } catch (_) { this.button.disabled = true; return; }
      this.storageKey = 'bb-month-cart-pending:' + this.root;
      try { this.pending = !!JSON.parse(global.localStorage.getItem(this.storageKey) || 'null'); }
      catch (_) { this.pending = false; } // Submission fails safely if storage cannot be used.
      this.addEventListener('change', () => this.update(), { signal: this.events.signal });
      this.button.addEventListener('click', event => { event.preventDefault(); this.submit(); }, { signal: this.events.signal });
      this.update();
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.requestController?.abort();
    }
    selected() {
      return this.checks.filter(c => c.checked && c.dataset.unavailable !== 'true')
        .map(c => ({ id: c.dataset.monthId, price: Number(c.dataset.price), year: c.dataset.year, title: c.dataset.title }));
    }
    update() {
      const selected = this.selected();
      const total = selectionTotal(selected);
      this.querySelector('[data-selection-summary]').textContent = selected.length ? selected.map(m => m.title).join(', ') : this.labels.empty;
      this.querySelector('[data-total]').textContent = this.money(total);
      const annualPrice = Number(this.dataset.annualPrice);
      const cost2027 = selectionTotal(selected.filter(m => m.year === '2027'));
      const extras = selected.filter(m => m.year === '2026');
      const extrasTotal = selectionTotal(extras);
      const annual = this.querySelector('[data-annual-message]');
      if (annual) {
        annual.hidden = !(annualPrice > 0 && cost2027 > annualPrice);
        const label = extras.length ? this.labels.annual_with_extras : this.labels.annual;
        const amounts = {
          savings: this.money(Math.max(0, cost2027 - annualPrice)),
          annual_price: this.money(annualPrice),
          extras_total: this.money(extrasTotal),
          combined_total: this.money(annualPrice + extrasTotal),
          count: String(extras.length)
        };
        annual.textContent = (label || this.labels.annual).replace(/\[(savings|annual_price|extras_total|combined_total|count)\]/g, (_, key) => amounts[key]);
      }
      this.button.disabled = !!this.busy || !selected.length || this.dataset.review === 'true';
      this.button.textContent = this.busy ? this.labels.adding : this.pending ? this.labels.check_status : this.labels.add.replace('[count]', selected.length).replace('[total]', this.money(total));
      this.setAttribute('aria-busy', this.busy ? 'true' : 'false');
    }
    async submit() {
      if (this.busy || this.dataset.review === 'true') return;
      const selected = this.selected();
      if (!selected.length) { this.status.textContent = this.labels.empty; return; }
      this.busy = true;
      this.checks.forEach(c => { c.disabled = true; });
      this.status.textContent = this.labels.adding; this.update();
      const perform = async () => {
        let state;
        try {
          const pending = JSON.parse(global.localStorage.getItem(this.storageKey) || 'null');
          if (pending !== null && (!Array.isArray(pending) || pending.some(id => !/^[1-9]\d*$/.test(id)))) fail('storage_error');
          state = { pending };
        } catch (_) { fail('storage_error'); }
        const save = pending => {
          try { global.localStorage.setItem(this.storageKey, JSON.stringify(pending)); }
          catch (_) { fail('storage_error'); }
          this.pending = !!pending;
        };
        const request = async (url, options) => {
          this.requestController = new AbortController();
          const timeout = global.setTimeout(() => this.requestController?.abort(), 15000);
          try {
            const response = await global.fetch(url, { ...options, signal: this.requestController.signal });
            const body = await response.json(); // Keep the timeout active while the response body downloads.
            return { ok: response.ok, status: response.status, json: async () => body };
          }
          finally { global.clearTimeout(timeout); }
        };
        return addMonths(selected.map(m => m.id), request, this.root, state, save);
      };
      try {
        // Serialize cooperating instances/tabs. Other cart writers still require a prelaunch integration test.
        if (global.navigator?.locks) await global.navigator.locks.request('bind-barn-month-cart', perform);
        else {
          if (global.__bbMonthCartBusy) fail('cart_read');
          global.__bbMonthCartBusy = true;
          try { await perform(); } finally { global.__bbMonthCartBusy = false; }
        }
        this.status.textContent = this.labels.success;
        // Full cart navigation refreshes the theme bag/count without guessed event contracts.
        global.location.assign(this.root + 'cart');
      } catch (error) { this.status.textContent = this.labels[error.message] || this.labels.unconfirmed; }
      finally {
        this.cartLink.hidden = false;
        this.busy = false;
        this.checks.forEach(c => { c.disabled = c.dataset.unavailable === 'true'; });
        this.update();
      }
    }
  }
  global.customElements.define('bb-month-picker', MonthPicker);
})(typeof window === 'undefined' ? globalThis : window);
